import * as Sentry from '@sentry/react';

import type { OnboardingStatus } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { PATHWAYS_ONBOARDING_EVENT_NAME } from 'bundles/onboarding-common/constants';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import useSetOnboardingSeen from 'bundles/onboarding-common/hooks/useSetOnboardingSeen';
import useUpdateUserProfile, { type UserProfileDataType } from 'bundles/onboarding-common/hooks/useUpdateUserProfile';
import type { LocalStorageOnboardingDataType } from 'bundles/onboarding-common/types/LocalStorage';
import { getIdontKnowOption } from 'bundles/onboarding-common/utils';
import { clearLocalStorageOnboardingData } from 'bundles/onboarding-common/utils/localStorageUtils';
import { mapAdvancerRoleValueForUserProfile } from 'bundles/onboarding-common/utils/userProfileUtils';

const useSaveLocalStorageOnboardingToLearnerObject = () => {
  const { updateUserProfile } = useUpdateUserProfile();
  const { setOnboardingSeen } = useSetOnboardingSeen(PATHWAYS_ONBOARDING_EVENT_NAME);
  const track = useTracker();

  const trackSubmitOnboarding = (onboardingStatus: OnboardingStatus) => {
    track('submit_onboarding', {
      form: {
        name: PATHWAYS_ONBOARDING_EVENT_NAME,
      },
      onboardingStatus,
    });
  };

  const saveLocalStorageOnboardingToLearnerObject = async (
    localStorageData: LocalStorageOnboardingDataType,
    userId: number,
    externalId: string,
    shouldClearSkills?: boolean
  ) => {
    const userProfileData: UserProfileDataType = {};

    Object.values(localStorageData).forEach((questionValue) => {
      switch (questionValue?.__typename) {
        case QUESTION_IDS.learningGoal:
          userProfileData.learningGoal = questionValue.value || undefined;
          break;
        case QUESTION_IDS.roleSelect:
          userProfileData.occupationPreferences = questionValue.value || undefined;
          break;
        case QUESTION_IDS.roleSelectAdvancer:
          const { occupationPreferences, currentOccupation } = mapAdvancerRoleValueForUserProfile(questionValue.value);
          userProfileData.occupationPreferences = occupationPreferences;
          userProfileData.currentOccupation = currentOccupation;
          break;
        case QUESTION_IDS.skillSelect:
          if (
            questionValue.value?.length &&
            questionValue.value.some((skill) => skill.id === getIdontKnowOption().id)
          ) {
            userProfileData.isUnsureOfSkills = true;
          } else {
            userProfileData.skillsWanted = questionValue.value || undefined;
          }
          break;
        case QUESTION_IDS.jobTitle:
          userProfileData.currentOccupation = questionValue.value || undefined;
          break;
        case QUESTION_IDS.educationLevel:
          userProfileData.educationLevel = questionValue.value || undefined;
          break;
        default:
          break;
      }
    });

    if (shouldClearSkills) {
      userProfileData.skillsWanted = [];
    }

    try {
      await updateUserProfile({
        ...userProfileData,
        userId,
        externalId,
      });
      trackSubmitOnboarding('success');
      clearLocalStorageOnboardingData();
    } catch (error) {
      Sentry.captureMessage('[onboarding] Error saving localStorage onboarding data to learner object', {
        extra: { error },
      });
      trackSubmitOnboarding('user_profile_api_error');
    }

    // Always try to set onboarding seen, even if saving local storage fails
    await setOnboardingSeen(() => trackSubmitOnboarding('onboarding_api_error'));
  };

  return { saveLocalStorageOnboardingToLearnerObject };
};

export default useSaveLocalStorageOnboardingToLearnerObject;
